/**
 * Formats a date string from DD.MM.YYYY to ISO YYYY-MM-DD.
 */
export function formatDateToISO(dateString: string): string {
    if (!dateString) {
        return dateString;
    }

    const isoPattern = /^\d{4}-\d{2}-\d{2}$/;
    if (isoPattern.test(dateString)) {
        return dateString;
    }

    const ddmmyyyyPattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
    const match = dateString.match(ddmmyyyyPattern);
    if (match?.[1] && match[2] && match[3]) {
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3];
        const normalized = `${year}-${month}-${day}`;
        const date = new Date(normalized);
        if (!isNaN(date.getTime()) && date.toISOString().startsWith(normalized)) {
            return normalized;
        }
    }

    return dateString;
}

/**
 * Parses an expert JSON string attribute of the shape {"id": "...", "name": "..."}.
 * Returns the parsed object only if both id and name are non-empty strings,
 * otherwise logs a warning and returns undefined.
 */
export function parseExpert(value: string): { id: string; name: string } | undefined {
    if (!value || !value.trim()) {
        return undefined;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(value);
    } catch {
        console.warn(`Invalid expert attribute, expected JSON string with id and name: ${value}`);
        return undefined;
    }

    const candidate = parsed as Record<string, unknown>;
    if (
        !!parsed &&
        typeof parsed === 'object' &&
        typeof candidate.id === 'string' &&
        candidate.id.trim() &&
        typeof candidate.name === 'string' &&
        candidate.name.trim()
    ) {
        return { id: candidate.id, name: candidate.name };
    }

    console.warn(`Invalid expert attribute, both id and name must be non-empty strings: ${value}`);
    return undefined;
}

export function formatDate(
    dateStr: string,
    locale: string,
    options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }
): string {
    const [day, month, year] = dateStr?.split('.') || [];

    if (day && month && year) {
        const date = new Date(`${year}-${month}-${day}`);
        return new Intl.DateTimeFormat(locale, options).format(date);
    }

    return '';
}
